import type { Language } from "./language";

/**
 * A noun's forms by the language's plural category (`Intl.PluralRules`): English has `one` and
 * `other`, Spanish too (its `many`, for millions, reads `other`); Russian and Ukrainian `one`
 * (1, 21), `few` (2-4, 22), `many` (5-20, 25) and `other` (fractions). A category a table leaves
 * out reads `other`.
 */
export interface PluralForms {
  readonly one: string;
  readonly few?: string;
  readonly many?: string;
  readonly other: string;
}

const RULES = new Map<Language, Intl.PluralRules>();

function rulesOf(language: Language): Intl.PluralRules {
  let rules = RULES.get(language);
  if (!rules) {
    rules = new Intl.PluralRules(language);
    RULES.set(language, rules);
  }
  return rules;
}

/** The form of the noun for `count` in `language`. */
export function plural(language: Language, count: number, forms: PluralForms): string {
  switch (rulesOf(language).select(count)) {
    case "one":
      return forms.one;
    case "few":
      return forms.few ?? forms.other;
    case "many":
      return forms.many ?? forms.other;
    default:
      return forms.other;
  }
}

/** The count and its noun: "1 hex", "3 hexes", "5 гексов". */
export function counted(language: Language, count: number, forms: PluralForms): string {
  return `${count} ${plural(language, count, forms)}`;
}
